import React, {
  useEffect,
  useRef,
  useState
} from 'react';
import classNames from 'classnames';
import {
  bool,
  func,
  number,
  oneOf,
  string
} from 'prop-types';
import {
  alias,
  arrayOf as arrayType,
  string as stringType,
  shape as shapeType
} from '@thd-nucleus/data-sources';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { Row } from '@thd-olt-component-react/grid';
import { Overlay } from '@thd-olt-component-react/overlay';
import { Sample } from '../Sample/Sample';
import { TraySample } from './components/TraySample';
import { TrayHeader } from './components/TrayHeader';
import { getCents, getDollars } from '../../helpers';
import { publish } from '../../analytics';
import { SamplesLoader } from '../../hooks/SamplesLoader';
import './sample-overlay.scss';

export const SampleOverlay = ({
  anchorSample,
  channel,
  dimensionText,
  open,
  onClose,
  onSampleData,
  oosMessage,
  parentId,
  sampleLimit,
  samplesPerRow,
  storeId,
  zipCode
}) => {

  const [gettingSamples, setGettingSamples] = useState(false);
  const [oosLoading, setOosLoading] = useState(false);
  const [traySamples, setTraySamples] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [currentPage, setPage] = useState(0);
  const pageCount = useRef(-1);
  const touchDevice = useRef();
  const sampleData = useRef([]);

  useEffect(() => {
    if (document && document.documentElement) {
      touchDevice.current = 'ontouchstart' in document.documentElement;
    }

    const orderSamples = sampleData.current;
    if (orderSamples?.length) {
      const defaultSample = orderSamples.find((sample) => sample.itemId === anchorSample) || orderSamples[0];
      const sampleInStock = !!defaultSample?.inStockStatus;

      if (sampleInStock) {
        setTraySamples([defaultSample]);
        setSubtotal(defaultSample?.price);
      }
    }

    // publish order samples click for analytics
    publish('order-sample.click');

    // remove selected samples on unmount
    return () => {
      setTraySamples([]);
    };
  }, [sampleData?.current.length]);

  const handleAddToCartClick = () => {
    // close overlay
    onClose();

    const skus = traySamples.map((sample) => sample.itemId);

    // for analytics
    const payload = {
      skus
    };

    publish('order-sample.atc', payload);
  };

  const isMobile = channel === 'mobile';
  const shouldExpandDiv = traySamples.length > 0 && isMobile;
  const shouldExpandDivMore = traySamples.length > samplesPerRow && isMobile;

  const headerTextClasses = classNames('sample-overlay__header__text', {
    'sample-overlay__header__text--small-font': isMobile
  });
  const contentClasses = classNames('sample-overlay__content', {
    'sample-overlay__content--expanded-one': shouldExpandDiv,
    'sample-overlay__content--expanded-two': shouldExpandDivMore
  });

  const deliveryClass = classNames({
    'sample-overlay__footer__delivery-absolute': channel === 'desktop',
    'sample-overlay__footer__delivery': channel === 'mobile'
  });

  const computeSubtotal = (amount) => {
    const calculatedSubtotal = amount + subtotal;
    setSubtotal(Math.abs(calculatedSubtotal));
  };

  const addSampleToTray = (sample, amount) => {
    setTraySamples([...traySamples, sample]);
    computeSubtotal(amount);
  };

  const removeSampleFromTray = (sample, amount) => {
    const { itemId } = sample;
    const updatedTray = traySamples.filter((samp) => samp.itemId !== itemId);
    setTraySamples(updatedTray);
    computeSubtotal(-amount);
  };

  const handleSampleClick = (sample) => {
    const { itemId, price = null } = sample;
    const sampleInTray = traySamples.find((samp) => samp.itemId === itemId);
    if (!sampleInTray) {
      addSampleToTray(sample, price);
    } else {
      removeSampleFromTray(sample, price);
    }
  };

  const getCartReqParams = (samples) => {
    return samples.map((sample) => {
      return {
        fulfillmentMethod: 'ShipToHome',
        fulfillmentLocation: zipCode,
        itemId: sample?.itemId,
        quantity: 1
      };
    });
  };

  const renderTraySamples = (samples) => {
    return samples.map((sample, sampleIndex) => {

      const { imgUrl, price = null } = sample;

      return (
        <div key={`tray-sample-${sampleIndex}`}>
          <TraySample
            channel={channel}
            onClick={() => removeSampleFromTray(sample, price)}
            imageUrl={imgUrl}
          />
        </div>
      );
    });
  };

  const expandedDivClasses = classNames('sample-overlay__expanded-div', {
    'sample-overlay__expanded-div--open': traySamples.length > 0 && isMobile
  });

  const expandDiv = (samples) => {
    return (
      <div className={expandedDivClasses}>
        <div className="sample-overlay__tray-samples-wrapper">
          <TrayHeader channel={channel} dimensions={dimensionText} subtotal={subtotal} />
          {renderTraySamples(samples)}
        </div>
      </div>
    );
  };

  const renderInStockScenario = (amount) => {
    return (
      <span className="sample-overlay__sample-content__price">
        {amount < 1
          ? <span>{getCents(amount)}&cent;</span>
          : <span>${getDollars(amount)}</span>}
      </span>
    );
  };

  const outOfStockMessage = (
    <span className="sample-overlay__sample-content__descriptor">
      <span className="sample-overlay__close-x-black" />
      Out of stock
    </span>
  );

  const discontinuedMessage = (
    <span className="sample-overlay__sample-content__descriptor">
      <span className="alert-inline alert-inline--danger">
        Discontinued
      </span>
    </span>
  );

  const renderSamples = (samples) => {
    let scenario;
    return samples.map((sample, sampleIndex) => {

      const {
        name = '',
        imgUrl = '',
        discontinued = false,
        inStockStatus = false,
        itemId = '',
        price = null,
      } = sample;

      const imageElements = imgUrl.split('/');
      if (imageElements.find((element) => element.includes('No_Image'))) {
        return null;
      }

      const buyable = inStockStatus && !discontinued;
      if (buyable) {
        scenario = renderInStockScenario(price);
      } else if (discontinued) {
        scenario = discontinuedMessage;
      } else {
        scenario = outOfStockMessage;
      }

      const selected = traySamples.find((samp) => samp.itemId === itemId);

      return (
        <div className="sample-overlay__col-custom-width" key={`sample-${sampleIndex}`}>
          <Sample
            channel={channel}
            imageUrl={imgUrl}
            onClick={buyable ? () => handleSampleClick(sample) : null}
            selected={!!selected}
            selectButton
            selectable={buyable}
            touchDevice={touchDevice?.current}
          >
            <div className="sample-overlay__sample-content">
              <span className="sample-overlay__sample-content__descriptor">{name}</span>
              {scenario}
            </div>
          </Sample>
        </div>
      );
    });
  };

  const handlePagination = (response) => {
    const { data, loading } = response;
    const { orderSamples, totalPages } = data || {};

    if (pageCount.current === -1 && totalPages) {
      pageCount.current = totalPages;
    }

    if (loading || !(orderSamples || []).length) {
      return;
    }
    const newSamples = orderSamples.filter((sample) => {
      return !sampleData.current.find((sd) => sd.itemId === sample.itemId);
    });

    sampleData.current = [...sampleData.current, ...newSamples];

    // NOTE: This is done to avoid race condition between updating the overlay content and the
    // on page loading indicator for the button.
    setTimeout(() => {
      if (currentPage < totalPages && !loading) {
        setPage(currentPage + 1);
      }
      setGettingSamples(totalPages > currentPage);
    }, 100);

  };

  useEffect(() => {
    const lastPage = pageCount.current === currentPage;
    if (lastPage && sampleData.current?.length && onSampleData && !gettingSamples) {
      onSampleData({ loading: false, orderSamples: sampleData.current, oosLoading });
    }
  }, [gettingSamples]);

  return (
    <SamplesLoader
      parentId={parentId}
      page={currentPage}
      sampleLimit={sampleLimit}
      storeId={storeId}
      zipCode={zipCode}
    >
      {(response) => {
        handlePagination(response);

        if (!sampleData?.current?.length || gettingSamples) {
          return null;
        }
        const outofStock = sampleData?.current?.length && sampleData.current.filter((sampleOos) => {
          return sampleOos.inStockStatus === true;
        });

        if (oosMessage && sampleData?.current?.length > 0 && outofStock.length === 0) {
          setOosLoading(true);
          return null;
        }

        return (
          <Overlay
            closeButton
            open={open}
            onClose={onClose}
            scrollable={false}
          >
            <>
              <Row className="sample-overlay__header">
                <span className={headerTextClasses}>
                  Choose Samples
                </span>
              </Row>
              <div className={contentClasses}>
                {renderSamples(sampleData.current || [])}
              </div>
              <Row className="sample-overlay__footer">
                <div className="sample-overlay__tray-header-wrapper">
                  <TrayHeader channel={channel} dimensions={dimensionText} subtotal={subtotal} />
                </div>
                <div className="sample-overlay__footer__purchase-wrapper">
                  <div className="sample-overlay__footer__samples">
                    {!traySamples.length && !isMobile
                      ? (
                        <span className="sample-overlay__footer__nosamples-text">
                          You currently have no samples selected.
                        </span>
                      )
                      : (
                        <div className="sample-overlay__samples-overflow-wrapper">
                          <div className="sample-overlay__footer__tray-samples">
                            {renderTraySamples(traySamples)}
                          </div>
                        </div>
                      )}
                  </div>
                  {expandDiv(traySamples)}
                  <div className="sample-overlay__footer__atc">
                    <AddToCart
                      cartOptions={{
                        channel,
                        directCheckout: true,
                        host: window.location.origin
                      }}
                      cartReqParams={getCartReqParams(traySamples)}
                      disabled={!traySamples.length}
                      onClick={() => handleAddToCartClick(traySamples)}
                    >
                      Add to Cart
                    </AddToCart>
                  </div>
                </div>
                <div className={deliveryClass}>
                  <span>
                    Fast and Free Delivery
                  </span>
                </div>
              </Row>
            </>
          </Overlay>
        );
      }}
    </SamplesLoader>

  );
};

SampleOverlay.displayName = 'SampleOverlay';

SampleOverlay.dataModel = {
  sampleProductsMetadata: alias('metadata').params({ parentId: stringType().isRequired() }).shape({
    childItemsLookup: arrayType(shapeType({
      attributeCombination: stringType(),
      itemId: stringType()
    }))
  })
};

SampleOverlay.propTypes = {
  anchorSample: string,
  channel: oneOf(['desktop', 'mobile', 'tablet']),
  dimensionText: string,
  onSampleData: func.isRequired,
  open: bool,
  onClose: func.isRequired,
  oosMessage: bool,
  parentId: string.isRequired,
  sampleLimit: string,
  samplesPerRow: number,
  storeId: string.isRequired,
  zipCode: string.isRequired
};

SampleOverlay.defaultProps = {
  anchorSample: null,
  channel: 'desktop',
  dimensionText: null,
  open: false,
  oosMessage: false,
  sampleLimit: '16', // don't switch to null, used in slice
  samplesPerRow: 6
};
